import { Link, useNavigate, useParams } from "react-router-dom";
import { useLessons } from "../data/LessonsContext";
import useLessonBlocks from "../data/useLessonBlocks";
import useProgress from "../data/useProgress";
import { COMPLETED, IN_PROGRESS } from "../data/progressStore";
import Markdown from "../components/Markdown";
import { BackIcon, CheckIcon, PlayIcon } from "../components/Icons";
import { LessonMeta } from "../components/LessonState";
import {
  LoadingCards,
  Message,
  NotAvailable,
  ProblemMessage,
  savedText,
} from "../components/Status";
import SetWork from "../components/SetWork";
import ui from "../components/ui.module.css";
import { subjectPath } from "./Week";

const LessonOverview = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const lessons = useLessons();
  const lesson = lessons.findLesson(slug);
  const blocks = useLessonBlocks(lesson?.id);
  const progress = useProgress();

  // Back up one level: the lesson's subject, or Today when it is not known.
  const back = (
    <Link to={lesson ? subjectPath(lesson.subjectKey) : "/"} className={ui.textButton}>
      <BackIcon />
      {lesson ? lesson.subject || "Lessons" : "Today"}
    </Link>
  );

  if (lessons.status !== "ready") {
    return (
      <div className={ui.page}>
        {back}
        {lessons.status === "loading" ? (
          <LoadingCards count={1} />
        ) : (
          <ProblemMessage problem={lessons.problem} onRetry={lessons.retry} autoRetrying={lessons.autoRetrying} />
        )}
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className={ui.page}>
        {back}
        <NotAvailable />
      </div>
    );
  }

  const record = progress.get(lesson.id);
  const stepCount = blocks.status === "ready" ? blocks.blocks.length : null;
  const canStart = stepCount > 0;

  const start = () => {
    progress.start(lesson.id, blocks.blocks[0]?.sort ?? 0);
    navigate(`/lesson/${lesson.slug}/teach`);
  };

  // A finished delivery stays finished; teaching the lesson again is a new
  // delivery with its own learner activity release.
  const again = () => {
    progress.teachAgain(lesson.id, blocks.blocks[0]?.sort ?? 0);
    navigate(`/lesson/${lesson.slug}/teach`);
  };

  let action;
  if (record?.status === COMPLETED) {
    action = (
      <>
        <button
          type="button"
          className={`${ui.button} ${ui.secondary}`}
          onClick={again}
          disabled={!canStart}
        >
          <PlayIcon />
          Teach again
        </button>
        <Link to={subjectPath(lesson.subjectKey)} className={`${ui.button} ${ui.primary} ${ui.grow}`}>
          Back to {lesson.subject || "lessons"}
        </Link>
      </>
    );
  } else {
    const resuming = record?.status === IN_PROGRESS;
    action = (
      <button
        type="button"
        className={`${ui.button} ${ui.primary}`}
        onClick={start}
        disabled={!canStart}
      >
        <PlayIcon />
        {resuming ? "Resume lesson" : "Start lesson"}
      </button>
    );
  }

  return (
    <>
      <div className={ui.page}>
        {back}
        <div className={ui.stack}>
          {lesson.studyArea && <p className={ui.eyebrow}>{lesson.studyArea}</p>}
          <h1 className={ui.title}>{lesson.title}</h1>
          <LessonMeta
            lesson={lesson}
            extra={
              stepCount > 0 && (
                <span>
                  {stepCount} {stepCount === 1 ? "step" : "steps"}
                </span>
              )
            }
          />
        </div>

        {record?.status === COMPLETED && (
          <p className={ui.notice}>
            <CheckIcon />
            Completed · {savedText(record)}
          </p>
        )}
        {record?.status === IN_PROGRESS && stepCount > 0 && (
          <p className={ui.notice}>
            You stopped at step {Math.min(record.step + 1, stepCount)} of{" "}
            {stepCount}.
          </p>
        )}

        {lesson.objectives && (
          <section className={`${ui.card} ${ui.section}`}>
            <h2>Learners will</h2>
            <Markdown className={ui.content}>{lesson.objectives}</Markdown>
          </section>
        )}

        {lesson.preparation && (
          <details className={ui.details}>
            <summary>How to prepare</summary>
            <Markdown className={ui.content}>{lesson.preparation}</Markdown>
          </details>
        )}

        {blocks.status === "ready" && <SetWork lessonId={lesson.id} blocks={blocks.blocks} />}

        {blocks.status === "loading" && (
          <LoadingCards label="Getting the lesson steps ready…" count={1} />
        )}
        {blocks.status === "error" && (
          <ProblemMessage problem={blocks.problem} onRetry={blocks.retry} autoRetrying={blocks.autoRetrying} />
        )}
        {blocks.status === "ready" && stepCount === 0 && (
          <Message title="This lesson has no steps yet">
            It can't be taught until its steps are ready.
          </Message>
        )}
      </div>

      <div className={ui.actionBar}>
        <div className={ui.actionBarInner}>{action}</div>
      </div>
    </>
  );
};

export default LessonOverview;
